import { RootState } from '@atredux/store'
import { ITool } from '@atredux/slices/toolSlice'
import { NO_CURRENT_STORE } from '@src/constants'
import { getSelectedStoreId, getStoreId, getStoreScanStatus } from '@atredux/selectors/storeSelector'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { getStoreScanPolicy } from '@services/ToolMenuConfigService'
import { getLaunchStoreId } from '@utils/LaunchStore'

/**
 * The store a (non-Associate Entry) tool is launched against. Uses the same getLaunchStoreId
 * rule as buildParams(), so the view-only decision here always matches the storeId and
 * viewOnly params the tool receives: view-only tools prefer the selected store, every other
 * tool the scanned-in store.
 */
export const getToolLaunchStoreId = (state: RootState, tool?: ITool | null): string | undefined => {
    const currentStoreId = getStoreId(state)
    const validCurrentStoreId = currentStoreId && currentStoreId !== NO_CURRENT_STORE ? currentStoreId : undefined
    return getLaunchStoreId(getStoreScanPolicy(tool) === 'viewOnly', validCurrentStoreId, getSelectedStoreId(state))
}

export const getSelectedToolLaunchStoreId = (state: RootState): string | undefined =>
    getToolLaunchStoreId(state, getSelectedTool(state))

/**
 * A tool is view-only when it is in the 'viewOnly' store-scan tier and the user has not
 * scanned into the store it launches against. No store, or an unknown scan status, is
 * view-only (fail-safe). Tools in the other tiers are never view-only: 'required' tools keep
 * the blocking store-scan modal and 'exempt' tools need no scan.
 */
export const isToolViewOnly = (state: RootState, tool?: ITool | null): boolean => {
    if (getStoreScanPolicy(tool) !== 'viewOnly') {
        return false
    }
    const storeId = getToolLaunchStoreId(state, tool)
    return !storeId || !getStoreScanStatus(state, storeId)
}

export const isSelectedToolViewOnly = (state: RootState): boolean =>
    isToolViewOnly(state, getSelectedTool(state))
